import React, { useCallback, useRef, useState } from 'react';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import InputCheck from '../../../../components/InputCheck';
import InputMaskAlternative from '../../../../components/InputMaskAlternative';
import InputWithoutTitle from '../../../../components/InputWithoutTitle';
import getValidationErrors from '../../../../utils/getValidationErrors';
import useQuery from '../../../../hooks/query';
import { useCurrentCallback } from '../../../../lib/use-current-effect';
import {
  activatePatient,
  identifyPatientActivation,
  LegalResponsibleData,
  PatientActivationConsentTerm,
  PatientActivationIdentity,
} from '../../../../services/patient/PatientActivation';

import {
  Container,
  ConsentTerm,
  Accept,
  Field,
  ResponsibleBlock,
  SaveButton,
  ShowTermButton,
} from './styles';

interface ConsentAndPasswordProps {
  identity: PatientActivationIdentity;
  cpf: string;
  birthday: string;
  /**
   * A conta nasceu aqui. `sessionToken` é a sessão de paciente que a ativação devolveu (0.34)
   * e que o passo do WhatsApp consome; `null` quando o back não conseguiu emiti-la, e nesse
   * caso quem decide pular o passo é a página, não esta tela.
   */
  onActivated: (dados: { sessionToken: string | null }) => void;
}

interface FormProps {
  password: string;
  passwordConfirmation: string;
  acceptedTerm: boolean;
  responsibleName?: string;
  responsibleCpf?: string;
  responsibleBirthday?: string;
}

/**
 * Passo 2: o termo de consentimento e a senha de acesso ao aplicativo.
 *
 * **O aceite é condição da ativação** (decisão de produto de 2026-08-19): sem base legal
 * para tratar o dado não há por que criar o acesso. O back também recusa sem
 * `consentTermId`, então a checagem daqui é conveniência de tela, não o gate.
 *
 * ⚠️ **Paciente menor de 18 precisa de responsável legal, e o cadastro pelo painel não
 * coleta um.** Quando o passo 1 diz `needsLegalResponsible`, os três campos aparecem e o
 * termo só é buscado depois de preenchidos — porque é o nome do responsável que entra no
 * texto. O nome do **paciente** nunca sai daqui para o servidor: ele vem do banco, sempre.
 */
const ConsentAndPassword: React.FC<ConsentAndPasswordProps> = ({
  identity,
  cpf,
  birthday,
  onActivated,
}) => {
  const formRef = useRef<FormHandles>(null);
  const query = useQuery();
  const [loading, setLoading] = useState(false);
  const [loadingTerm, setLoadingTerm] = useState(false);
  const [consentTerm, setConsentTerm] =
    useState<PatientActivationConsentTerm | null>(identity.consentTerm);
  const [legalResponsible, setLegalResponsible] =
    useState<LegalResponsibleData>();

  const token = query.get('token') ?? '';

  const responsibleFromForm = useCallback(():
    | LegalResponsibleData
    | undefined => {
    const dados = formRef.current?.getData() as FormProps | undefined;

    if (
      !dados?.responsibleName ||
      !dados?.responsibleCpf ||
      !dados?.responsibleBirthday
    ) {
      return undefined;
    }

    return {
      name: dados.responsibleName,
      cpf: dados.responsibleCpf,
      birthday: dados.responsibleBirthday,
    };
  }, []);

  /**
   * Busca o termo já preenchido com o responsável informado. É a mesma rota do passo 1 —
   * ela aceita o responsável justamente para que o texto seja montado no servidor, com o
   * nome do paciente vindo do banco.
   */
  const handleShowTerm = useCurrentCallback(
    (isCurrent) => async () => {
      const responsavel = responsibleFromForm();

      if (!responsavel) {
        toast.error('Preencha os dados do responsável legal.');
        return;
      }

      setLoadingTerm(true);

      try {
        const identidade = await identifyPatientActivation({
          token,
          cpf,
          birthday,
          legalResponsible: responsavel,
        });

        if (isCurrent()) {
          setConsentTerm(identidade.consentTerm);
          setLegalResponsible(responsavel);
          setLoadingTerm(false);
        }
      } catch (err) {
        if (err.response) {
          toast.error(err.response.data.message);
        } else {
          toast.error('Entre em contato com a equipe que fez o seu cadastro.');
        }

        setLoadingTerm(false);
      }
    },
    [birthday, cpf, responsibleFromForm, token],
  );

  const handleSubmit = useCurrentCallback(
    (isCurrent) => async (data: FormProps) => {
      formRef.current?.setErrors({});

      if (!consentTerm) {
        toast.error('Leia o termo de consentimento antes de continuar.');
        return;
      }

      // O `InputCheck` só pinta a borda quando há erro, sem texto: o aviso do aceite vai
      // no toast, para não haver como não concluir sem saber por quê.
      if (!data.acceptedTerm) {
        toast.error('É necessário aceitar o termo de consentimento.');
        return;
      }

      setLoading(true);

      try {
        const schema = Yup.object().shape({
          password: Yup.string()
            .matches(
              /^(?=.*?[a-z])(?=.*?[A-Z])(?=.*?[0-9])(?=.*?[\W|_]).*$/,
              'Senha deve conter pelo menos uma letra maiúscula, uma letra minúscula, um número e um caractere especial',
            )
            .min(8, 'Mínimo de 8 dígitos'),
          passwordConfirmation: Yup.string().oneOf(
            [Yup.ref('password'), null],
            'Senhas não coincidem',
          ),
        });

        await schema.validate(data, { abortEarly: false });

        const { token: sessionToken } = await activatePatient({
          token,
          cpf,
          birthday,
          password: data.password,
          passwordConfirmation: data.passwordConfirmation,
          consentTermId: consentTerm.id,
          ...(legalResponsible ? { legalResponsible } : {}),
        });

        if (isCurrent()) {
          setLoading(false);
          onActivated({ sessionToken });
        }
      } catch (err) {
        if (err instanceof Yup.ValidationError) {
          const errors = getValidationErrors(err);
          formRef.current?.setErrors(errors);
        } else if (err.response) {
          toast.error(err.response.data.message);
        } else {
          toast.error('Entre em contato com a equipe que fez o seu cadastro.');
        }

        setLoading(false);
      }
    },
    [consentTerm, legalResponsible, token, cpf, birthday, onActivated],
  );

  return (
    <Container>
      <h1>Termo de consentimento e senha</h1>
      <h2>{`${identity.name}, falta pouco para concluir o seu cadastro.`}</h2>

      <Form ref={formRef} onSubmit={handleSubmit}>
        {identity.needsLegalResponsible && (
          <ResponsibleBlock>
            <p>
              O cadastro é de uma pessoa menor de 18 anos: informe os dados de
              quem responde legalmente por ela.
            </p>
            <Field>
              <label>Nome do responsável:</label>
              <InputWithoutTitle name="responsibleName" />
            </Field>
            <Field>
              <label>CPF do responsável:</label>
              <InputMaskAlternative
                name="responsibleCpf"
                type="text"
                title=""
                placeholder="000.000.000-00"
                mask="999.999.999-99"
              />
            </Field>
            <Field>
              <label>Data de nascimento do responsável:</label>
              <InputWithoutTitle
                className="datepicker"
                name="responsibleBirthday"
                type="date"
                max={new Date().toISOString().split('T')[0]}
              />
            </Field>
            <ShowTermButton
              loading={loadingTerm}
              type="button"
              onClick={handleShowTerm}
            >
              Ver o termo
            </ShowTermButton>
          </ResponsibleBlock>
        )}

        {consentTerm && (
          <>
            <ConsentTerm>{consentTerm.content}</ConsentTerm>
            <Accept>
              <InputCheck name="acceptedTerm" defaultChecked={false} />
              <span>Li e aceito o termo de consentimento acima.</span>
            </Accept>

            {/**
             * "senha de acesso **ao aplicativo**" saiu na 0.35: o passo seguinte e a tela
             * final pararam de falar em app — o da loja é o descontinuado —, e deixar a
             * menção só aqui faria a mesma ativação se contradizer no meio do caminho.
             */}
            <p>
              Crie a sua senha de acesso. Use no mínimo 8 caracteres, com pelo
              menos uma letra maiúscula, uma minúscula, um número e um caractere
              especial.
            </p>
            <Field>
              <label>Senha:</label>
              <InputWithoutTitle name="password" type="password" />
            </Field>
            <Field>
              <label>Repita a senha:</label>
              <InputWithoutTitle name="passwordConfirmation" type="password" />
            </Field>
            <SaveButton loading={loading} type="submit">
              Concluir
            </SaveButton>
          </>
        )}
      </Form>
    </Container>
  );
};

export default ConsentAndPassword;
